const MAX_OBJECT_CONFIGURATION_TRACE_EVENTS = 64;

export type ObjectConfigurationTarget = 'doc' | 'folder';
export type ObjectConfigurationTraceData = Record<
  string,
  boolean | number | string | null | undefined
>;

export interface ObjectConfigurationTraceEvent {
  atMs: number;
  data?: ObjectConfigurationTraceData;
  name: string;
}

export interface ObjectConfigurationTraceSnapshot {
  durationMs: number;
  events: readonly ObjectConfigurationTraceEvent[];
  longTaskMaxMs: number;
  longTaskTotalMs: number;
  longTasks: number;
  requests: Readonly<Record<string, number>>;
  target: ObjectConfigurationTarget;
}

interface ActiveTrace {
  endedAt?: number;
  events: ObjectConfigurationTraceEvent[];
  longTasks: number[];
  observer?: PerformanceObserver;
  requests: Record<string, number>;
  startedAt: number;
  target: ObjectConfigurationTarget;
}

let activeTrace: ActiveTrace | null = null;

const now = () =>
  typeof performance === 'undefined' ? Date.now() : performance.now();
const rounded = (value: number) => Math.round(value * 10) / 10;

function snapshot(trace: ActiveTrace): ObjectConfigurationTraceSnapshot {
  return {
    durationMs: rounded((trace.endedAt ?? now()) - trace.startedAt),
    events: trace.events.map(event => ({
      ...event,
      ...(event.data ? { data: { ...event.data } } : {}),
    })),
    longTaskMaxMs: rounded(Math.max(0, ...trace.longTasks)),
    longTaskTotalMs: rounded(
      trace.longTasks.reduce((total, duration) => total + duration, 0)
    ),
    longTasks: trace.longTasks.length,
    requests: { ...trace.requests },
    target: trace.target,
  };
}

declare global {
  interface Window {
    /** Safe diagnostics: timings and aggregate counts only; no object/user IDs. */
    __streamlainObjectConfigurationTrace?: () => ObjectConfigurationTraceSnapshot | null;
  }
}

export function beginObjectConfigurationTrace(
  target: ObjectConfigurationTarget
): void {
  activeTrace?.observer?.disconnect();
  const trace: ActiveTrace = {
    events: [],
    longTasks: [],
    requests: {},
    startedAt: now(),
    target,
  };
  activeTrace = trace;

  if (typeof window !== 'undefined') {
    window.__streamlainObjectConfigurationTrace = () =>
      activeTrace ? snapshot(activeTrace) : null;
  }
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      trace.observer = new PerformanceObserver(list => {
        if (trace.endedAt !== undefined) return;
        trace.longTasks.push(
          ...list.getEntries().map(entry => rounded(entry.duration))
        );
      });
      trace.observer.observe({ entryTypes: ['longtask'] });
    } catch {
      trace.observer = undefined;
    }
  }
  recordObjectConfigurationTraceEvent('clicked');
}

export function recordObjectConfigurationTraceEvent(
  name: string,
  data?: ObjectConfigurationTraceData
): void {
  const trace = activeTrace;
  if (!trace || trace.endedAt !== undefined) return;
  trace.events.push({
    atMs: rounded(now() - trace.startedAt),
    ...(data ? { data } : {}),
    name,
  });
  if (trace.events.length > MAX_OBJECT_CONFIGURATION_TRACE_EVENTS) {
    trace.events.shift();
  }
  try {
    performance.mark(`streamlain.object-configuration.${trace.target}.${name}`);
  } catch {
    // Diagnostics must never affect object configuration.
  }
}

export function endObjectConfigurationTrace(
  target: ObjectConfigurationTarget,
  name = 'ended'
): void {
  const trace = activeTrace;
  if (!trace || trace.target !== target || trace.endedAt !== undefined) return;
  recordObjectConfigurationTraceEvent(name);
  trace.endedAt = now();
  trace.observer?.disconnect();
  trace.observer = undefined;
}

export async function traceObjectConfigurationRequest<T>(
  name: string,
  request: () => Promise<T>
): Promise<T> {
  const trace = activeTrace;
  if (trace && trace.endedAt === undefined) {
    trace.requests[name] = (trace.requests[name] ?? 0) + 1;
    recordObjectConfigurationTraceEvent('request-started', { request: name });
  }
  try {
    return await request();
  } finally {
    if (trace && trace === activeTrace && trace.endedAt === undefined) {
      recordObjectConfigurationTraceEvent('request-finished', {
        request: name,
      });
    }
  }
}
